/* #region(collapsed) DEVELOPER DOCUMENTATION ════════════════════════════════════ */
/**
  @license
  • COPYRIGHT NOTICE:
    © CROUIN 2021. All rights reserved. Powered by MaxDesign.
    ⚠ FR : CONTENU PROTÉGÉ PAR LES DROITS D'AUTEURS (article L. 111-1 du code de la propriété intellectuelle). Cette ressource ne peut pas être utilisée, diffusée ou exploitée sans l'approbation de ses auteurs. Pour plus d'informations, veuillez contacter admin(at)maxdesign.expert
    ⚠ EN : CONTENT PROTECTED WITH AUTHOR COPYRIGHTS (section L. 111-1 of the intellectual property code). This resource may not be used, distributed or exploited without the approval of its authors. For any information request, please contact admin(at)maxdesign.expert
 
  • INTRODUCTION:
    Code powered by MaxDesign.
    This code uses HTML custom element shipping-estimate-maxdesign.
    Custom elements extend HTML by creating new elements that carry their own JavaScript for adding new behavior.
 
  • MORE INFORMATION:
    Contact us at admin(at)maxdesign.expert
*/
/* #endregion ════════════════════════════════════════════════════════════════════ */

class ShippingEstimate extends HTMLElement {
  static holidays = new Map();

  connectedCallback() {
    const config = this.querySelector(':scope > [type="application/json"]');
    if (!config) return;
    this.settings = JSON.parse(config.textContent);
    this.updateEstimate();
    if (this.querySelector('[data-shipping-flag]')) this.updateFlag();
  }

  async updateEstimate() {
    const { delayMin, delayMax, weekday, skipSat, skipSun, skipHol, language, country, timeZone } = this.settings;
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone || 'Europe/Paris',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const part = name => Number(parts.find(item => item.type === name).value);
    const today = new Date(Date.UTC(part('year'), part('month') - 1, part('day')));
    const min = Math.max(0, Math.floor(Number(delayMin) || 0));
    const max = Math.max(min, Math.floor(Number(delayMax) || 0));
    let holidays = new Set();
    if (skipHol) {
      try {
        const years = [today.getUTCFullYear(), today.getUTCFullYear() + 1];
        const lists = await Promise.all(years.map(year => this.getHolidays(year, country)));
        holidays = new Set(lists.flat().map(item => item.date));
      } catch (_) {
        // Keep a weekend-aware estimate when the holiday service is unavailable.
      }
    }
    const addDays = days => {
      const date = new Date(today);
      while (days > 0) {
        date.setUTCDate(date.getUTCDate() + 1);
        if (
          (skipSat && date.getUTCDay() === 6) ||
          (skipSun && date.getUTCDay() === 0) ||
          holidays.has(date.toISOString().slice(0, 10))
        )
          continue;
        days--;
      }
      return date;
    };
    const start = addDays(min),
      end = addDays(max);
    const locale = `${(language || 'fr').split('-')[0]}-${country || 'FR'}`;
    const options = { timeZone: 'UTC', weekday: weekday ? 'short' : undefined, day: 'numeric' };
    const differentMonth = start.getUTCMonth() !== end.getUTCMonth() || start.getUTCFullYear() !== end.getUTCFullYear();
    const endText = end.toLocaleDateString(locale, { ...options, month: 'short' });
    const text =
      start.getTime() === end.getTime()
        ? endText
        : `${start.toLocaleDateString(locale, { ...options, month: differentMonth || weekday ? 'short' : undefined })} - ${endText}`;
    const placeholder = this.querySelector('[data-shipping-estimate]');
    if (placeholder && this.isConnected) placeholder.textContent = text;
  }

  getHolidays(year, country) {
    const key = `${year}/${country}`;
    if (!ShippingEstimate.holidays.has(key)) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 1500);
      const request = fetch(`https://date.nager.at/api/v3/publicholidays/${key}`, { signal: controller.signal })
        .then(response => {
          if (!response.ok) throw new Error('Holiday service unavailable');
          return response.json();
        })
        .then(data => {
          if (!Array.isArray(data)) throw new Error('Invalid holiday response');
          return data;
        })
        .finally(() => clearTimeout(timer));
      ShippingEstimate.holidays.set(key, request);
    }
    return ShippingEstimate.holidays.get(key);
  }

  async updateFlag() {
    try {
      const response = await fetch(`${Shopify.routes.root || '/'}browsing_context_suggestions.json`);
      const data = await response.json();
      const country = data?.detected_values?.country?.handle;
      if (!/^[A-Z]{2}$/i.test(country || '') || !this.isConnected) return;
      const image = document.createElement('img');
      image.src = `https://cdn.shopify.com/static/images/flags/${country.toLowerCase()}.svg?width=30`;
      image.alt = country.toUpperCase();
      this.querySelector('[data-shipping-flag]')?.replaceChildren(image);
    } catch (_) {}
  }
}
if (!customElements.get('shipping-estimate-maxdesign')) {
  customElements.define('shipping-estimate-maxdesign', ShippingEstimate);
}
